const TYPOGRAPHY_MAP = {
  h0: "heading-xl",
  h1: "heading-xl",
  h2: "heading-lg",
  h3: "heading-md",
  h4: "heading-sm",
  h5: "heading-xs",
  h6: "body-bold",
  large: "body-lg",
  body: "body",
  bodyHeading: "body-bold",
  bodySmall: "body-sm",
  bodySmallHeading: "body-sm-bold",
  finePrint: "body-xs",
  finePrintHeading: "body-xs-bold",
  legal: "fineprint"
};
const RESPONSIVE_TYPOGRAPHY_MAP = Object.fromEntries(Object.entries(TYPOGRAPHY_MAP).map(([k, v]) => [k, `responsive.${v}`]));
function migrateTypographyToken(v8FontType) {
  if (!v8FontType) return v8FontType;
  return TYPOGRAPHY_MAP[v8FontType] ?? v8FontType;
}
function migrateTypographyTokenResponsive(v8FontType) {
  if (!v8FontType) return v8FontType;
  return RESPONSIVE_TYPOGRAPHY_MAP[v8FontType] ?? v8FontType;
}
const FONT_FAMILY_MAP = {
  sansSerif: "heading",
  serif: "heading-alt"
};
function migrateFontFamily(v8FontFamily) {
  if (!v8FontFamily) return v8FontFamily;
  return FONT_FAMILY_MAP[v8FontFamily] ?? v8FontFamily;
}
const SPACING_MAP = {
  none: "none",
  xxs: "tightest",
  xs: "tighter",
  sm: "default",
  md: "loose",
  lg: "looser",
  xl: "layout.looser",
  xxl: "layout.vast.default",
  xxxl: "layout.vast.loose"
};
const NEGATIVE_SPACING_MAP = Object.fromEntries(Object.entries(SPACING_MAP).filter(([k]) => k !== "none").map(([k, v]) => [`-${k}`, `-${v}`]));
const FULL_SPACING_MAP = {
  ...SPACING_MAP,
  ...NEGATIVE_SPACING_MAP
};
const GRIDBASE = 8;
const PX_TO_SPACING_NAME = Object.fromEntries(Object.entries({
  none: 0,
  xxs: 4,
  xs: 8,
  sm: 16,
  md: 24,
  lg: 32,
  xl: 48
}).map(([k, px]) => [px, k]));
function migrateSpacingToken(v8Spacing) {
  if (v8Spacing === void 0 || v8Spacing === null || v8Spacing === "") return v8Spacing;
  if (v8Spacing in FULL_SPACING_MAP) return FULL_SPACING_MAP[v8Spacing];
  const num = Number(v8Spacing);
  if (!Number.isNaN(num) && num !== 0) {
    const px = GRIDBASE * num;
    const name = PX_TO_SPACING_NAME[px];
    if (name) return FULL_SPACING_MAP[name];
  }
  return v8Spacing;
}
const COLOR_MAP = {
  "": "text.neutral",
  // default body text #111116
  textWhite: "white",
  // #FFFFFF (exact; onImpact.neutral-fixed rendered off-white #F7F7F7)
  textLight: "gray.400",
  // #ADADB6 (official preset; was text.subtle — collapsed onto textMedium)
  textMedium: "text.subtle",
  // #535364
  textDark: "text.neutral",
  // #111116
  textBrand: "text.accent.brand.hero",
  // #0041D9
  brand: "text.accent.brand.hero",
  // #0041D9 (exact; same as textBrand — offered by the adornmentColor picker)
  textAqua: "aqua.400",
  // #31CCE6 → #00C3DC (closest; no exact v10 primitive)
  textGreen: "green.300",
  // #9FF17B (exact)
  textOrange: "orange.300",
  // #FFC0AB (exact)
  textPurple: "purple.300",
  // #E6A8FF (exact)
  textDarkBlue: "blue.900",
  // #001962 (exact)
  textDarkOrange: "orange.600",
  // #D03C0B (exact)
  textDarkPurple: "purple.500",
  // #891EFA (exact)
  textTeal: "teal.600",
  // #136F65 (exact)
  // Background colors (v8 backgroundX → v10). Targets follow the approved
  // "CMS v8 → v10 Color Mapping" doc (Justin Wade + Mahesh Lingala signed off
  // 2026-06-29). Each background is one of:
  //   • Use Fix  — override to the v10 token that reproduces the exact v8
  //                SkunkWorks tint (kept where design wanted the original).
  //   • No fix   — keep the v10 "refreshed" value that renders today (the
  //                team chose to align with Constellation rather than preserve
  //                the v8 hex).
  //   • Deprecate — retired in v10 (render transparent); replace in content.
  // Hex in comments = render value under the `zillow` (Visual Refresh) theme.
  // Background entries stay inert until background-using components adopt
  // migrateColorToken (the Group block still resolves via the preset today).
  // Use Fix — preserve the original v8 SkunkWorks color:
  backgroundLight: "gray.50",
  // #F7F7F7 (exact; design kept the lighter tint)
  backgroundDark: "gray.800",
  // #2A2A33 (exact)
  backgroundAqua: "aqua.100",
  // #D3F6FD (exact)
  backgroundGreen: "green.100",
  // #DAFACE (exact)
  backgroundOrange: "orange.100",
  // #FFE9E3 (exact)
  backgroundPurple: "purple.100",
  // #F7E3FF (exact)
  backgroundDarkBlue: "blue.900",
  // #001962 (exact)
  backgroundDarkOrange: "orange.600",
  // #D03C0B (exact)
  backgroundTeal: "teal.600",
  // #136F65 (exact; v10 default renders nothing)
  // No fix — keep the v10 refreshed value that renders today. Mapped to the
  // static primitive the refreshed semantic token resolves to under `zillow`
  // (rather than the semantic token itself) so the color stays fixed and does
  // not invert in dark mode, matching the static v8 behavior.
  backgroundWhite: "white",
  // #FFFFFF (bg.neutral)
  backgroundBrand: "blue.600",
  // #0041D9 (bg.accent.brand.hero)
  backgroundBlue: "blue.100",
  // #E2F0FF
  backgroundRed: "red.400",
  // #FF7F73 (bg.accent.red.soft)
  backgroundYellow: "yellow.300",
  // #FFC752 (bg.accent.yellow.soft)
  backgroundDarkAqua: "aqua.800",
  // #00343F (bg.accent.aqua.impact)
  backgroundDarkGreen: "green.800",
  // #0B3800 (bg.accent.green.impact)
  backgroundDarkPurple: "purple.800",
  // #3B0470 (bg.accent.purple.impact)
  backgroundDarkRed: "red.800",
  // #5A0514 (bg.accent.red.impact)
  backgroundDarkYellow: "yellow.800"
  // #4C1F00 (bg.accent.yellow.impact)
  // Deprecate — backgroundOrangeRed / backgroundDarkOrangeRed are retired in
  // v10 (render transparent); replace in content rather than remapping (e.g.
  // Santa's House post 20882). Intentionally not mapped.
};
function migrateColorToken(v8Color) {
  if (v8Color === void 0 || v8Color === null) return v8Color;
  return COLOR_MAP[v8Color] ?? v8Color;
}
export {
  COLOR_MAP,
  FONT_FAMILY_MAP,
  FULL_SPACING_MAP,
  RESPONSIVE_TYPOGRAPHY_MAP,
  SPACING_MAP,
  TYPOGRAPHY_MAP,
  migrateColorToken,
  migrateFontFamily,
  migrateSpacingToken,
  migrateTypographyToken,
  migrateTypographyTokenResponsive
};
